package presentation.screens.webPlayer

import androidx.lifecycle.SavedStateHandle
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.model.Route
import features.watch.WatchProgressInteractor
import utils.BaseViewModel
import utils.launch
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class WebPlayerViewModel(
    private val savedStateHandle: SavedStateHandle,
    private val navigator: Navigator,
    private val watchProgressInteractor: WatchProgressInteractor,
) : BaseViewModel<WebPlayerState, WebPlayerMVIAction>() {

    private val route = savedStateHandle.toRoute<Route.Dialog.WebPlayer>()

    private var pendingEngagedSec = 0.0
    private var lastPositionSec = -1.0
    private var wasPaused = true
    private var lastFlushMs = 0L
    private var latestPositionSec = 0.0
    private var latestDurationSec = 0.0
    private var latestAudio: String? = null
    private var latestQuality: String? = null
    private var latestSubtitles: String? = null

    override fun initState(): WebPlayerState = WebPlayerState(
        videoUrl = route.url,
        title = route.title,
    )

    init {
        launch {
            val resume = watchProgressInteractor.resumeSeconds(
                videoId = route.videoId,
                isTvShow = route.isTvShow,
                seasonNumber = route.seasonNumber,
                episodeNumber = route.episodeNumber,
            )
            val choice = watchProgressInteractor.savedChoice(
                videoId = route.videoId,
                isTvShow = route.isTvShow,
                seasonNumber = route.seasonNumber,
                episodeNumber = route.episodeNumber,
            )
            if (latestAudio == null) latestAudio = choice.audio
            if (latestQuality == null) latestQuality = choice.quality
            if (latestSubtitles == null) latestSubtitles = choice.subtitles
            updateState {
                copy(
                    resumePositionSec = resume,
                    resumeAudio = choice.audio,
                    resumeQuality = choice.quality,
                    resumeSubtitles = choice.subtitles,
                    playbackChoiceReady = true,
                )
            }
        }
    }

    override fun onAction(action: WebPlayerMVIAction) {
        when (action) {
            is WebPlayerMVIAction.OnBackPressed -> {
                launch {
                    flush()
                    navigator.back()
                }
            }

            is WebPlayerMVIAction.ProgressSample -> onSample(
                positionSec = action.positionSec,
                durationSec = action.durationSec,
                paused = action.paused,
            )

            is WebPlayerMVIAction.PlaybackChoiceChanged -> onChoice(
                audio = action.audio,
                quality = action.quality,
                subtitles = action.subtitles,
            )
        }
    }

    private fun onChoice(audio: String?, quality: String?, subtitles: String?) {
        var changed = false
        audio.remembered()?.let {
            if (it != latestAudio) {
                latestAudio = it
                changed = true
            }
        }
        quality.remembered()?.let {
            if (it != latestQuality) {
                latestQuality = it
                changed = true
            }
        }
        subtitles.remembered()?.let {
            if (it != latestSubtitles) {
                latestSubtitles = it
                changed = true
            }
        }
        if (changed && latestDurationSec > 0) launch { flush() }
    }

    private fun onSample(positionSec: Double, durationSec: Double, paused: Boolean) {
        if (route.videoId <= 0 || !positionSec.isFinite() || !durationSec.isFinite() || durationSec <= 0) return
        if (!paused && lastPositionSec >= 0) {
            val delta = positionSec - lastPositionSec
            if (delta in 0.05..2.5) pendingEngagedSec += delta
        }
        lastPositionSec = positionSec
        latestPositionSec = positionSec
        latestDurationSec = durationSec
        val now = Clock.System.now().toEpochMilliseconds()
        val pauseEdge = paused && !wasPaused
        wasPaused = paused
        val due = pendingEngagedSec >= 15.0 || now - lastFlushMs >= 20_000L
        if (pauseEdge || due) {
            launch { flush() }
        }
    }

    private suspend fun flush() {
        if (route.videoId <= 0 || latestDurationSec <= 0) return
        val delta = pendingEngagedSec.roundToInt()
        pendingEngagedSec -= delta
        if (pendingEngagedSec < 0) pendingEngagedSec = 0.0
        lastFlushMs = Clock.System.now().toEpochMilliseconds()
        watchProgressInteractor.record(
            videoId = route.videoId,
            imdbId = route.imdbId,
            isTvShow = route.isTvShow,
            seasonNumber = route.seasonNumber,
            episodeNumber = route.episodeNumber,
            title = route.title,
            imageUrl = route.imageUrl,
            backdropUrl = route.backdropUrl,
            positionSec = latestPositionSec.roundToInt().coerceAtLeast(0),
            durationSec = latestDurationSec.roundToInt().coerceAtLeast(1),
            engagedDeltaSec = delta.coerceAtLeast(0),
            audio = latestAudio,
            quality = latestQuality,
            subtitles = latestSubtitles,
        )
    }
}

private fun String?.remembered(): String? = this?.trim()?.takeIf { it.isNotEmpty() }
